import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  Bell,
  Boxes,
  HardDrive,
  Layers3,
  Network,
  RefreshCw,
  Send,
  Server,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { workspaceAdminAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';
import { EmptyState, StatSkeletonGrid } from '../components/common/States';

const PANEL_CLASS = 'rounded-3xl border border-border bg-card text-foreground shadow-sm';
const SUB_PANEL_CLASS = 'rounded-2xl border border-border bg-muted';
const LABEL_CLASS = 'text-[11px] uppercase tracking-[0.18em] text-muted-foreground';
const HERO_CARD_CLASS =
  'group relative overflow-hidden border border-border bg-gradient-to-br from-primary/10 via-card to-info/10 text-foreground shadow-[0_22px_60px_rgba(15,23,42,0.08)]';
const HERO_METRIC_CARD_CLASS =
  'rounded-2xl border border-border bg-card/85 p-4 shadow-sm backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:shadow-md';
const HERO_ACTION_CARD_CLASS =
  'group flex h-full flex-col justify-between rounded-2xl border border-border bg-card/90 p-4 text-left text-foreground shadow-sm transition duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-md';

const statusTone = {
  healthy: 'bg-emerald-500/10 text-emerald-600',
  active: 'bg-emerald-500/10 text-emerald-600',
  completed: 'bg-emerald-500/10 text-emerald-600',
  delivered: 'bg-emerald-500/10 text-emerald-600',
  queued: 'bg-amber-500/10 text-amber-600',
  running: 'bg-cyan-500/10 text-cyan-600',
  warning: 'bg-amber-500/10 text-amber-600',
  degraded: 'bg-amber-500/10 text-amber-600',
  failed: 'bg-rose-500/10 text-rose-600',
  error: 'bg-rose-500/10 text-rose-600',
};

const formatStorage = (gigabytes) => `${Number(gigabytes || 0).toFixed(2)} GB`;
const statusCount = (items, key) => (items || []).find((item) => item.name === key)?.value || 0;
const MG_METRIC_OPTIONS = [
  { value: 'attendance_users', label: 'Users Who Marked Attendance' },
  { value: 'active_users', label: 'Active Users' },
];

const MONETIZATION_FEATURE_LABELS = {
  gamification: 'Gamification',
  amazon_voucher_addon: 'Amazon Voucher Addon',
  ai_challenges: 'AI Challenges',
  advanced_leaderboards: 'Advanced Leaderboards',
  department_competitions: 'Department Competitions',
  enterprise_rewards: 'Enterprise Rewards',
};
const formatCurrency = (value, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency || 'INR',
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const PLATFORM_CORE_SECTIONS = [
  {
    key: 'overview',
    id: 'platform-core/overview',
    label: 'Overview',
    description: 'Command center snapshot and quick actions',
    icon: Sparkles,
  },
  {
    key: 'foundation',
    id: 'platform-core/foundation',
    label: 'Foundation',
    description: 'Foundation coverage and API gateway readiness',
    icon: Layers3,
  },
  {
    key: 'monetization',
    id: 'platform-core/monetization',
    label: 'Monetization',
    description: 'Revenue analytics and subscription controls',
    icon: ShieldCheck,
  },
  {
    key: 'provisioning',
    id: 'platform-core/provisioning',
    label: 'Provisioning',
    description: 'Queue and process tenant provisioning jobs',
    icon: Boxes,
  },
  {
    key: 'services',
    id: 'platform-core/services',
    label: 'Services',
    description: 'Service registry and runtime health',
    icon: Server,
  },
  {
    key: 'notifications',
    id: 'platform-core/notifications',
    label: 'Notifications',
    description: 'Providers, templates, queue state, and delivery',
    icon: Bell,
  },
  {
    key: 'storage',
    id: 'platform-core/storage',
    label: 'Storage & Activity',
    description: 'Storage assets and platform-level activity',
    icon: HardDrive,
  },
];

const SuperadminPlatformCorePage = ({ activeTab = 'platform-core/overview', onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [bootstrapLoading, setBootstrapLoading] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [processingQueue, setProcessingQueue] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const [provisioningLoading, setProvisioningLoading] = useState(false);
  const [processingProvisioningJobId, setProcessingProvisioningJobId] = useState(null);
  const [savingMonetizationPlanId, setSavingMonetizationPlanId] = useState(null);
  const [planOptions, setPlanOptions] = useState([]);
  const [billingAccounts, setBillingAccounts] = useState([]);
  const [monetizationPlans, setMonetizationPlans] = useState([]);
  const [provisioningForm, setProvisioningForm] = useState({
    name: '',
    slug: '',
    tenant_type: 'enterprise',
    isolation_level: 'shared',
    billing_model: 'parent_paid',
    plan_id: 'auto',
    billing_account_id: 'none',
  });
  const [dashboard, setDashboard] = useState({
    summary: {},
    foundation_modules: [],
    services: [],
    api_gateway: { routes_total: 0, public_routes: 0, authenticated_routes: 0, top_routes: [] },
    notifications: { total: 0, status_mix: [], providers: [], template_rows: [], provider_rows: [], recent: [] },
    provisioning: { total_jobs: 0, status_mix: [], recent_jobs: [] },
    storage: { objects_total: 0, storage_gb: 0, providers: [], recent_assets: [] },
    activity: { events_7d: 0, recent: [] },
    monetization: {
      subscription_controls: [],
      feature_matrix: [],
      pricing_controls: { supported_metrics: [] },
      revenue_analytics: { plan_mix: [], tenant_rows: [], upsell_rows: [] },
    },
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [dashboardResponse, plansResponse, billingResponse] = await Promise.all([
        workspaceAdminAPI.getPlatformCoreDashboard(),
        workspaceAdminAPI.listSubscriptionPlans({ status: 'active' }),
        workspaceAdminAPI.listBillingAccounts(),
      ]);
      const nextDashboard = dashboardResponse.data || {};
      setDashboard(nextDashboard);
      setMonetizationPlans(nextDashboard.monetization?.subscription_controls || []);
      setPlanOptions(plansResponse.data?.items || plansResponse.data || []);
      setBillingAccounts(billingResponse.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load platform core dashboard'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const summaryCards = useMemo(
    () => [
      {
        key: 'tenants',
        label: 'Active Tenants',
        value: dashboard.summary?.active_tenants || 0,
        detail: `${dashboard.summary?.tenants_total || 0} total tenants`,
        icon: Layers3,
      },
      {
        key: 'users',
        label: 'Verified Identities',
        value: dashboard.summary?.verified_users || 0,
        detail: `${dashboard.summary?.users_total || 0} total users`,
        icon: ShieldCheck,
      },
      {
        key: 'services',
        label: 'Healthy Services',
        value: dashboard.summary?.healthy_services || 0,
        detail: `${dashboard.services?.length || 0} registered services`,
        icon: Server,
      },
      {
        key: 'routes',
        label: 'API Routes',
        value: dashboard.summary?.api_routes || 0,
        detail: `${dashboard.api_gateway?.authenticated_routes || 0} protected`,
        icon: Network,
      },
      {
        key: 'notifications',
        label: 'Queued Notifications',
        value: dashboard.summary?.queued_notifications || 0,
        detail: `${dashboard.summary?.notification_templates || 0} templates • ${dashboard.summary?.active_notification_providers || 0} active providers`,
        icon: Bell,
      },
      {
        key: 'storage',
        label: 'Storage Footprint',
        value: formatStorage(dashboard.summary?.storage_gb),
        detail: `${dashboard.summary?.stored_assets || 0} indexed objects`,
        icon: HardDrive,
      },
      {
        key: 'provisioning',
        label: 'Queued Provisioning',
        value: dashboard.summary?.queued_provisioning_jobs || 0,
        detail: `${dashboard.summary?.provisioning_jobs || 0} total jobs`,
        icon: Boxes,
      },
    ],
    [dashboard]
  );

  const handleBootstrap = useCallback(async () => {
    try {
      setBootstrapLoading(true);
      await workspaceAdminAPI.bootstrapPlatformCore();
      await loadData();
      toast.success('Platform core bootstrapped');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to bootstrap platform core'));
    } finally {
      setBootstrapLoading(false);
    }
  }, [loadData]);

  const handleQueueNotification = useCallback(async () => {
    try {
      setNotificationLoading(true);
      await workspaceAdminAPI.queuePlatformNotification({
        recipient: currentUserEmailFallback(dashboard),
        channel: 'in_app',
        template_key: 'platform.ops.heartbeat',
        payload_json: { scope: 'phase-0', actor_email: currentUserEmailFallback(dashboard) },
        priority: 'normal',
      });
      await loadData();
      toast.success('Notification queued');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to queue notification'));
    } finally {
      setNotificationLoading(false);
    }
  }, [dashboard, loadData]);

  const handleProcessQueue = useCallback(async () => {
    try {
      setProcessingQueue(true);
      const response = await workspaceAdminAPI.processPlatformNotificationQueue({ limit: 25 });
      await loadData();
      toast.success(
        `Processed ${response.data?.processed || 0} notifications • delivered ${response.data?.delivered || 0}`
      );
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to process notification queue'));
    } finally {
      setProcessingQueue(false);
    }
  }, [loadData]);

  const handleLogActivity = useCallback(async () => {
    try {
      setActivityLoading(true);
      await workspaceAdminAPI.createPlatformActivity({
        event_type: 'platform_health_review',
        summary: 'Superadmin captured a platform-core health checkpoint.',
        event_category: 'operations',
        source_service: 'superadmin-dashboard',
        severity: 'info',
        metadata_json: { module: 'platform-core', review: 'manual' },
      });
      await loadData();
      toast.success('Platform activity recorded');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to record platform activity'));
    } finally {
      setActivityLoading(false);
    }
  }, [loadData]);

  const heroActionCards = useMemo(
    () => [
      {
        key: 'bootstrap',
        label: bootstrapLoading ? 'Bootstrapping...' : 'Bootstrap Platform Core',
        value: `${dashboard.services?.length || 0} services`,
        detail: `${dashboard.summary?.api_routes || 0} routes • ${dashboard.notifications?.templates_total || 0} templates • ${dashboard.storage?.objects_total || 0} assets`,
        icon: Boxes,
        onClick: handleBootstrap,
        disabled: bootstrapLoading,
      },
      {
        key: 'queue-notification',
        label: notificationLoading ? 'Queueing...' : 'Queue Notification',
        value: `${dashboard.summary?.queued_notifications || 0} queued`,
        detail: `${dashboard.notifications?.providers_total || 0} providers • ${dashboard.notifications?.templates_total || 0} templates • ${dashboard.notifications?.recent?.[0]?.status || 'no recent sends'}`,
        icon: Send,
        onClick: handleQueueNotification,
        disabled: notificationLoading,
      },
      {
        key: 'process-queue',
        label: processingQueue ? 'Processing...' : 'Process Queue',
        value: `${statusCount(dashboard.notifications?.status_mix, 'delivered')} delivered`,
        detail: `${dashboard.summary?.queued_notifications || 0} queued • ${statusCount(dashboard.notifications?.status_mix, 'blocked')} blocked • ${dashboard.notifications?.attempts_total || 0} attempts`,
        icon: Bell,
        onClick: handleProcessQueue,
        disabled: processingQueue,
      },
      {
        key: 'log-activity',
        label: activityLoading ? 'Logging...' : 'Log Activity',
        value: `${dashboard.activity?.events_7d || 0} events / 7d`,
        detail: dashboard.activity?.recent?.[0]?.summary || 'No platform activity recorded yet',
        icon: Activity,
        onClick: handleLogActivity,
        disabled: activityLoading,
      },
    ],
    [
      activityLoading,
      bootstrapLoading,
      dashboard,
      handleBootstrap,
      handleLogActivity,
      handleProcessQueue,
      handleQueueNotification,
      notificationLoading,
      processingQueue,
    ]
  );

  const currentSectionKey = useMemo(() => {
    const rawSection = String(activeTab || '')
      .replace(/^platform-core\/?/, '')
      .trim()
      .toLowerCase();
    if (!rawSection || rawSection === 'platform-core') {
      return 'overview';
    }
    return PLATFORM_CORE_SECTIONS.some((section) => section.key === rawSection) ? rawSection : 'overview';
  }, [activeTab]);

  const currentSection = useMemo(
    () => PLATFORM_CORE_SECTIONS.find((section) => section.key === currentSectionKey) || PLATFORM_CORE_SECTIONS[0],
    [currentSectionKey]
  );

  const navigationCards = useMemo(
    () => [
      {
        ...PLATFORM_CORE_SECTIONS.find((section) => section.key === 'overview'),
        metric: `${dashboard.summary?.active_tenants || 0} active tenants`,
      },
      {
        ...PLATFORM_CORE_SECTIONS.find((section) => section.key === 'foundation'),
        metric: `${dashboard.foundation_modules?.length || 0} foundation modules`,
      },
      {
        ...PLATFORM_CORE_SECTIONS.find((section) => section.key === 'monetization'),
        metric: `${monetizationPlans.length || 0} plan controls`,
      },
      {
        ...PLATFORM_CORE_SECTIONS.find((section) => section.key === 'provisioning'),
        metric: `${dashboard.provisioning?.total_jobs || 0} provisioning jobs`,
      },
      {
        ...PLATFORM_CORE_SECTIONS.find((section) => section.key === 'services'),
        metric: `${dashboard.services?.length || 0} registered services`,
      },
      {
        ...PLATFORM_CORE_SECTIONS.find((section) => section.key === 'notifications'),
        metric: `${dashboard.summary?.queued_notifications || 0} queued notifications`,
      },
      {
        ...PLATFORM_CORE_SECTIONS.find((section) => section.key === 'storage'),
        metric: `${dashboard.storage?.objects_total || 0} stored assets`,
      },
    ],
    [dashboard, monetizationPlans.length]
  );

  const handleProvisioningFieldChange = (field, value) => {
    setProvisioningForm((current) => ({
      ...current,
      [field]: value,
      ...(field === 'name'
        ? {
            slug:
              current.slug && current.slug.trim().length > 0
                ? current.slug
                : value
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-+|-+$/g, '')
                    .slice(0, 80),
          }
        : {}),
    }));
  };

  const handleCreateProvisioningJob = async () => {
    try {
      setProvisioningLoading(true);
      await workspaceAdminAPI.createTenantProvisioningJob({
        slug: provisioningForm.slug,
        name: provisioningForm.name,
        tenant_type: provisioningForm.tenant_type,
        isolation_level: provisioningForm.isolation_level,
        billing_model: provisioningForm.billing_model,
        plan_id: provisioningForm.plan_id === 'auto' ? null : Number(provisioningForm.plan_id),
        billing_account_id: provisioningForm.billing_account_id === 'none' ? null : Number(provisioningForm.billing_account_id),
      });
      await loadData();
      setProvisioningForm((current) => ({ ...current, name: '', slug: '' }));
      toast.success('Provisioning job queued');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to queue provisioning job'));
    } finally {
      setProvisioningLoading(false);
    }
  };

  const handleProcessProvisioningJob = async (jobId) => {
    try {
      setProcessingProvisioningJobId(jobId);
      await workspaceAdminAPI.processTenantProvisioningJob(jobId, { record_activity: true });
      await loadData();
      toast.success('Provisioning job processed');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to process provisioning job'));
    } finally {
      setProcessingProvisioningJobId(null);
    }
  };

  const monetizationAnalytics = dashboard.monetization?.revenue_analytics || {};
  const monetizationPricing = dashboard.monetization?.pricing_controls || { supported_metrics: [] };

  const updateMonetizationPlan = (planId, updater) => {
    setMonetizationPlans((current) =>
      current.map((plan) => (plan.id === planId || plan.canonical_code === planId ? updater(plan) : plan))
    );
  };

  const handleMonetizationFeatureToggle = (planId, featureKey, checked) => {
    updateMonetizationPlan(planId, (plan) => ({
      ...plan,
      feature_matrix: {
        ...(plan.feature_matrix || {}),
        [featureKey]: Boolean(checked),
      },
    }));
  };

  const handleMonetizationPricingChange = (planId, field, value) => {
    updateMonetizationPlan(planId, (plan) => ({
      ...plan,
      pricing_controls: {
        ...(plan.pricing_controls || {}),
        [field]: value,
      },
    }));
  };

  const handleSaveMonetizationPlan = async (plan) => {
    if (!plan?.id) {
      toast.error('Create the missing subscription tier from the subscription catalog before saving monetization controls.');
      return;
    }

    const pricingControls = plan.pricing_controls || {};
    const payload = {
      monthly_price: Number(plan.pricing_controls?.monthly_price ?? plan.monthly_price ?? 0),
      yearly_price: Number(plan.pricing_controls?.annual_price ?? plan.yearly_price ?? 0),
      feature_matrix: plan.feature_matrix || {},
      pricing_controls: {
        per_employee_price: Number(pricingControls.per_employee_price || 0),
        usage_based_price: Number(pricingControls.usage_based_price || 0),
        usage_metric: pricingControls.usage_metric || 'api_calls',
        usage_unit: Number(pricingControls.usage_unit || 1000),
        currency: pricingControls.currency || 'INR',
        mg_users: Number(pricingControls.mg_users || 0),
        mg_metric: pricingControls.mg_metric || 'attendance_users',
        additional_user_price: Number(pricingControls.additional_user_price || 0),
        additional_user_step: Number(pricingControls.additional_user_step || 1),
      },
    };

    try {
      setSavingMonetizationPlanId(plan.id);
      await workspaceAdminAPI.updateSubscriptionPlan(plan.id, payload);
      await loadData();
      toast.success(`${plan.name || plan.code} monetization controls saved`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save monetization controls'));
    } finally {
      setSavingMonetizationPlanId(null);
    }
  };

  const renderOverviewSection = () => (
    <div className="space-y-6">
      <Card className={HERO_CARD_CLASS}>
        <CardContent className="pt-6">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -left-16 top-0 h-40 w-40 rounded-full bg-primary/10 blur-3xl transition duration-700 group-hover:scale-110" />
            <div className="absolute right-0 top-10 h-44 w-44 rounded-full bg-info/10 blur-3xl transition duration-700 group-hover:scale-110" />
          </div>
          <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
            <div className="space-y-5">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-card/80 px-3 py-1 text-xs font-medium text-primary shadow-sm backdrop-blur-sm">
                <Sparkles className="h-3.5 w-3.5 text-primary transition-transform duration-300 group-hover:rotate-12" />
                Phase 0 foundation layer
              </div>
              <div>
                <h2 className="text-3xl font-semibold tracking-tight text-foreground">Platform Core Command Center</h2>
                <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
                  Operate multi-tenant control, identity, billing, notifications, storage, auditability, and gateway readiness from one superadmin surface.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {summaryCards.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.key} className={HERO_METRIC_CARD_CLASS}>
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">{item.label}</p>
                        <Icon className="h-4 w-4 text-primary" />
                      </div>
                      <p className="mt-3 text-2xl font-semibold text-foreground">{item.value}</p>
                      <p className="mt-2 text-xs text-muted-foreground">{item.detail}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-3">
              <Button className="bg-primary text-primary-foreground shadow-sm transition hover:-translate-y-0.5 hover:bg-primary/90" onClick={loadData}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                {heroActionCards.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      className={HERO_ACTION_CARD_CLASS}
                      onClick={item.onClick}
                      disabled={item.disabled}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">{item.label}</p>
                          <p className="mt-3 text-2xl font-semibold text-foreground">{item.value}</p>
                        </div>
                        <Icon className="h-4 w-4 shrink-0 text-primary transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </div>
                      <p className="mt-3 text-xs leading-5 text-muted-foreground">{item.detail}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className={PANEL_CLASS}>
        <CardHeader>
          <CardTitle>Platform Core Sections</CardTitle>
          <CardDescription>Open a focused sub-page for each platform-core capability instead of scanning one long dashboard.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {navigationCards
            .filter((section) => section.key !== 'overview')
            .map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => onNavigate?.(section.id)}
                  className="card-interactive rounded-2xl border border-border bg-muted p-5 text-left transition hover:border-primary/40 hover:bg-card"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className={LABEL_CLASS}>{section.label}</p>
                      <p className="mt-2 text-lg font-semibold text-foreground">{section.metric}</p>
                    </div>
                    <Icon className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="mt-3 text-sm text-muted-foreground">{section.description}</p>
                </button>
              );
            })}
        </CardContent>
      </Card>
    </div>
  );

  const renderFoundationSection = () => (
    <div className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <Card className={PANEL_CLASS}>
          <CardHeader>
            <CardTitle>Foundation Coverage</CardTitle>
            <CardDescription>Phase 0 readiness across architecture, identity, governance, billing, messaging, storage, and observability.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            {(dashboard.foundation_modules || []).map((module) => (
              <div key={module.key} className={`${SUB_PANEL_CLASS} p-4`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className={LABEL_CLASS}>{module.label}</p>
                    <p className="mt-2 text-2xl font-semibold text-foreground">{module.value}</p>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium capitalize ${statusTone[module.status] || 'bg-muted text-muted-foreground'}`}>
                    {module.status}
                  </span>
                </div>
                <p className="mt-3 text-sm text-muted-foreground">{module.detail}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className={PANEL_CLASS}>
          <CardHeader>
            <CardTitle>API Gateway Readiness</CardTitle>
            <CardDescription>Route registry, auth posture, and service ownership for the platform edge.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className={`${SUB_PANEL_CLASS} p-4`}>
                <p className={LABEL_CLASS}>Total Routes</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">{dashboard.api_gateway?.routes_total || 0}</p>
              </div>
              <div className={`${SUB_PANEL_CLASS} p-4`}>
                <p className={LABEL_CLASS}>Public</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">{dashboard.api_gateway?.public_routes || 0}</p>
              </div>
              <div className={`${SUB_PANEL_CLASS} p-4`}>
                <p className={LABEL_CLASS}>Protected</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">{dashboard.api_gateway?.authenticated_routes || 0}</p>
              </div>
            </div>
            <div className="space-y-3">
              {(dashboard.api_gateway?.top_routes || []).map((route) => (
                <div key={route.route_key} className="rounded-2xl border border-border bg-muted p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-foreground">{route.path}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{route.method} • {route.upstream_service}</p>
                    </div>
                    <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${route.auth_policy === 'public' ? 'bg-cyan-500/10 text-cyan-600' : 'bg-indigo-500/10 text-indigo-600'}`}>
                      {route.auth_policy}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderMonetizationSection = () => (
    <div className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <Card className={PANEL_CLASS}>
          <CardHeader>
            <CardTitle>Monetization Analytics</CardTitle>
            <CardDescription>Database-backed revenue, adoption, engagement, and upsell signals across gamification subscriptions.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              <div className={`${SUB_PANEL_CLASS} p-4`}>
                <p className={LABEL_CLASS}>Gamification Revenue</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">
                  {formatCurrency(monetizationAnalytics.gamification_revenue)}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">Estimated recurring value from DB-priced gamification plans</p>
              </div>
              <div className={`${SUB_PANEL_CLASS} p-4`}>
                <p className={LABEL_CLASS}>Voucher Revenue</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">
                  {formatCurrency(monetizationAnalytics.voucher_revenue)}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">Issued voucher value routed through the voucher database workflow</p>
              </div>
              <div className={`${SUB_PANEL_CLASS} p-4`}>
                <p className={LABEL_CLASS}>Tenant Adoption Rate</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">
                  {Number(monetizationAnalytics.tenant_adoption_rate || 0).toFixed(2)}%
                </p>
                <p className="mt-2 text-xs text-muted-foreground">Active tenants with live gamification module usage</p>
              </div>
              <div className={`${SUB_PANEL_CLASS} p-4`}>
                <p className={LABEL_CLASS}>Engagement Scores</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">
                  {Number(monetizationAnalytics.engagement_score || 0).toFixed(2)}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">Average XP-derived engagement score from employee progress records</p>
              </div>
              <div className={`${SUB_PANEL_CLASS} p-4`}>
                <p className={LABEL_CLASS}>Upsell Conversions</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">{monetizationAnalytics.upsell_conversions || 0}</p>
                <p className="mt-2 text-xs text-muted-foreground">Upgrade events inferred from subscription assignment audit history</p>
              </div>
            </div>
            <div className="grid gap-3 xl:grid-cols-2">
              <div className="space-y-3">
                <p className={LABEL_CLASS}>Plan Mix</p>
                {(monetizationAnalytics.plan_mix || []).map((row) => (
                  <div key={row.name} className="rounded-2xl border border-border bg-muted p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-foreground">{row.name}</p>
                      <p className="text-sm text-muted-foreground">{row.value} tenants</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="space-y-3">
                <p className={LABEL_CLASS}>Recent Upsells</p>
                {(monetizationAnalytics.upsell_rows || []).length === 0 ? (
                  <EmptyState icon={Activity} title="No upsell conversions" description="Upsell conversions will appear here once recorded." compact />
                ) : (
                  (monetizationAnalytics.upsell_rows || []).map((row, index) => (
                    <div key={`${row.target}-${index}`} className="rounded-2xl border border-border bg-muted p-4">
                      <p className="text-sm font-semibold text-foreground">{row.target}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {String(row.from_plan || '').toUpperCase()} to {String(row.to_plan || '').toUpperCase()}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="space-y-3">
              <p className={LABEL_CLASS}>Top Revenue Tenants</p>
              {(monetizationAnalytics.tenant_rows || []).map((row) => (
                <div key={row.tenant_id} className="rounded-2xl border border-border bg-muted p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-foreground">{row.tenant_name}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {row.plan_name} • {row.active_users} active users • score {Number(row.engagement_score || 0).toFixed(2)}
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-foreground">{formatCurrency(row.estimated_mrr)}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className={PANEL_CLASS}>
          <CardHeader>
            <CardTitle>Feature Matrix Coverage</CardTitle>
            <CardDescription>Plan-level enablement for EPIC 10 premium controls driven entirely by subscription plan records.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            {(dashboard.monetization?.feature_matrix || []).map((feature) => (
              <div key={feature.key} className={`${SUB_PANEL_CLASS} p-4`}>
                <p className={LABEL_CLASS}>{feature.label}</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">{feature.enabled_plan_count || 0}</p>
                <p className="mt-2 text-xs text-muted-foreground">Active plans with this feature enabled</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className={PANEL_CLASS}>
        <CardHeader>
          <CardTitle>Subscription Controls</CardTitle>
          <CardDescription>Manage Starter, Growth, Scale, and Enterprise pricing plus premium feature entitlements without hardcoded seeding.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 xl:grid-cols-2">
          {monetizationPlans.map((plan) => (
            <div key={plan.id || plan.canonical_code} className="rounded-2xl border border-border bg-muted p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className={LABEL_CLASS}>{plan.canonical_code || plan.code}</p>
                  <h3 className="mt-2 text-lg font-semibold text-foreground">{plan.name}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {plan.is_missing
                      ? 'No database plan row exists for this tier yet.'
                      : `${plan.subscription_count || 0} subscriptions • ${plan.active_tenant_count || 0} active tenants • ${formatCurrency(plan.estimated_mrr, plan.pricing_controls?.currency)} estimated MRR`}
                  </p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${plan.is_missing ? 'bg-amber-500/10 text-amber-700' : 'bg-emerald-500/10 text-emerald-700'}`}>
                  {plan.is_missing ? 'missing' : 'live'}
                </span>
              </div>

              <div className="mt-5 grid gap-3 md:grid-cols-2">
                <div>
                  <p className={LABEL_CLASS}>Monthly Pricing</p>
                  <Input
                    value={plan.pricing_controls?.monthly_price ?? ''}
                    onChange={(event) => handleMonetizationPricingChange(plan.id || plan.canonical_code, 'monthly_price', event.target.value)}
                    disabled={plan.is_missing}
                  />
                </div>
                <div>
                  <p className={LABEL_CLASS}>Annual Pricing</p>
                  <Input
                    value={plan.pricing_controls?.annual_price ?? ''}
                    onChange={(event) => handleMonetizationPricingChange(plan.id || plan.canonical_code, 'annual_price', event.target.value)}
                    disabled={plan.is_missing}
                  />
                </div>
                <div>
                  <p className={LABEL_CLASS}>Per Employee Pricing</p>
                  <Input
                    value={plan.pricing_controls?.per_employee_price ?? ''}
                    onChange={(event) => handleMonetizationPricingChange(plan.id || plan.canonical_code, 'per_employee_price', event.target.value)}
                    disabled={plan.is_missing}
                  />
                </div>
                <div>
                  <p className={LABEL_CLASS}>Usage Based Pricing</p>
                  <Input
                    value={plan.pricing_controls?.usage_based_price ?? ''}
                    onChange={(event) => handleMonetizationPricingChange(plan.id || plan.canonical_code, 'usage_based_price', event.target.value)}
                    disabled={plan.is_missing}
                  />
                </div>
                <div>
                  <p className={LABEL_CLASS}>Usage Metric</p>
                  <Select
                    value={plan.pricing_controls?.usage_metric || 'api_calls'}
                    onValueChange={(value) => handleMonetizationPricingChange(plan.id || plan.canonical_code, 'usage_metric', value)}
                    disabled={plan.is_missing}
                  >
                    <SelectTrigger><SelectValue placeholder="Select metric" /></SelectTrigger>
                    <SelectContent>
                      {(monetizationPricing.supported_metrics || []).map((metric) => (
                        <SelectItem key={metric.value} value={metric.value}>
                          {metric.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className={LABEL_CLASS}>Usage Unit</p>
                  <Input
                    value={plan.pricing_controls?.usage_unit ?? ''}
                    onChange={(event) => handleMonetizationPricingChange(plan.id || plan.canonical_code, 'usage_unit', event.target.value)}
                    disabled={plan.is_missing}
                  />
                </div>
                <div>
                  <p className={LABEL_CLASS}>Minimum Guarantee (Users)</p>
                  <Input
                    value={plan.pricing_controls?.mg_users ?? ''}
                    onChange={(event) => handleMonetizationPricingChange(plan.id || plan.canonical_code, 'mg_users', event.target.value)}
                    disabled={plan.is_missing}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">Users covered by the monthly price. 0 disables MG.</p>
                </div>
                <div>
                  <p className={LABEL_CLASS}>Minimum Guarantee Metric</p>
                  <Select
                    value={plan.pricing_controls?.mg_metric || 'attendance_users'}
                    onValueChange={(value) => handleMonetizationPricingChange(plan.id || plan.canonical_code, 'mg_metric', value)}
                    disabled={plan.is_missing}
                  >
                    <SelectTrigger><SelectValue placeholder="Select metric" /></SelectTrigger>
                    <SelectContent>
                      {MG_METRIC_OPTIONS.map((metric) => (
                        <SelectItem key={metric.value} value={metric.value}>
                          {metric.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className={LABEL_CLASS}>Additional User Price</p>
                  <Input
                    value={plan.pricing_controls?.additional_user_price ?? ''}
                    onChange={(event) => handleMonetizationPricingChange(plan.id || plan.canonical_code, 'additional_user_price', event.target.value)}
                    disabled={plan.is_missing}
                  />
                </div>
                <div>
                  <p className={LABEL_CLASS}>Additional User Step</p>
                  <Input
                    value={plan.pricing_controls?.additional_user_step ?? ''}
                    onChange={(event) => handleMonetizationPricingChange(plan.id || plan.canonical_code, 'additional_user_step', event.target.value)}
                    disabled={plan.is_missing}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">Extra users bill in blocks of this size (partial blocks round up).</p>
                </div>
              </div>

              <div className="mt-5 space-y-3">
                <p className={LABEL_CLASS}>Feature Matrix</p>
                {Object.entries(plan.feature_matrix || {}).map(([featureKey, enabled]) => (
                  <div key={featureKey} className="flex items-center justify-between rounded-xl border border-border bg-card px-3 py-2">
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {MONETIZATION_FEATURE_LABELS[featureKey] || featureKey}
                      </p>
                    </div>
                    <Switch
                      checked={Boolean(enabled)}
                      onCheckedChange={(checked) => handleMonetizationFeatureToggle(plan.id || plan.canonical_code, featureKey, checked)}
                      disabled={plan.is_missing}
                    />
                  </div>
                ))}
              </div>

              <div className="mt-5 flex justify-end">
                <Button
                  onClick={() => handleSaveMonetizationPlan(plan)}
                  disabled={plan.is_missing || savingMonetizationPlanId === plan.id}
                >
                  {savingMonetizationPlanId === plan.id ? 'Saving...' : 'Save Monetization Controls'}
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );

  const renderProvisioningSection = () => (
    <Card className={PANEL_CLASS}>
      <CardHeader>
        <CardTitle>Tenant Provisioning</CardTitle>
        <CardDescription>Queue tenant bootstrap jobs, process them, and monitor step-level orchestration state.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <p className={LABEL_CLASS}>Tenant Name</p>
            <Input value={provisioningForm.name} onChange={(event) => handleProvisioningFieldChange('name', event.target.value)} placeholder="Acme People Ops" />
          </div>
          <div>
            <p className={LABEL_CLASS}>Tenant Slug</p>
            <Input value={provisioningForm.slug} onChange={(event) => handleProvisioningFieldChange('slug', event.target.value)} placeholder="acme-people-ops" />
          </div>
          <div>
            <p className={LABEL_CLASS}>Tenant Type</p>
            <Select value={provisioningForm.tenant_type} onValueChange={(value) => handleProvisioningFieldChange('tenant_type', value)}>
              <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="enterprise">Enterprise</SelectItem>
                <SelectItem value="subsidiary">Subsidiary</SelectItem>
                <SelectItem value="partner">Partner</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className={LABEL_CLASS}>Isolation</p>
            <Select value={provisioningForm.isolation_level} onValueChange={(value) => handleProvisioningFieldChange('isolation_level', value)}>
              <SelectTrigger><SelectValue placeholder="Select isolation" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="shared">Shared</SelectItem>
                <SelectItem value="isolated">Isolated</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className={LABEL_CLASS}>Billing Model</p>
            <Select value={provisioningForm.billing_model} onValueChange={(value) => handleProvisioningFieldChange('billing_model', value)}>
              <SelectTrigger><SelectValue placeholder="Select billing model" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="parent_paid">Parent Paid</SelectItem>
                <SelectItem value="tenant_paid">Tenant Paid</SelectItem>
                <SelectItem value="platform_paid">Platform Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className={LABEL_CLASS}>Plan</p>
            <Select value={provisioningForm.plan_id} onValueChange={(value) => handleProvisioningFieldChange('plan_id', value)}>
              <SelectTrigger><SelectValue placeholder="Select plan" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto Assign</SelectItem>
                {planOptions.map((plan) => (
                  <SelectItem key={plan.id} value={String(plan.id)}>
                    {plan.name || plan.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <p className={LABEL_CLASS}>Billing Account</p>
            <Select value={provisioningForm.billing_account_id} onValueChange={(value) => handleProvisioningFieldChange('billing_account_id', value)}>
              <SelectTrigger><SelectValue placeholder="Select billing account" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Billing Account</SelectItem>
                {billingAccounts.map((account) => (
                  <SelectItem key={account.id} value={String(account.id)}>
                    {account.account_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={handleCreateProvisioningJob} disabled={provisioningLoading || !provisioningForm.name.trim() || !provisioningForm.slug.trim()}>
            {provisioningLoading ? 'Queueing...' : 'Queue Provisioning Job'}
          </Button>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {(dashboard.provisioning?.status_mix || []).map((item) => (
            <div key={item.name} className={`${SUB_PANEL_CLASS} p-4`}>
              <p className={LABEL_CLASS}>{item.name}</p>
              <p className="mt-2 text-2xl font-semibold text-foreground">{item.value}</p>
            </div>
          ))}
        </div>
        <div className="space-y-3">
          {(dashboard.provisioning?.recent_jobs || []).map((job) => (
            <div key={job.job_uuid} className="rounded-2xl border border-border bg-muted p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-foreground">{job.tenant_name}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{job.tenant_slug} • {job.tenant_type} • {job.isolation_level}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium capitalize ${statusTone[job.status] || 'bg-muted text-muted-foreground'}`}>
                    {job.status}
                  </span>
                  {['queued', 'failed', 'retry'].includes(job.status) ? (
                    <Button variant="outline" className="h-8 px-3" onClick={() => handleProcessProvisioningJob(job.id)} disabled={processingProvisioningJobId === job.id}>
                      {processingProvisioningJobId === job.id ? 'Running...' : 'Process'}
                    </Button>
                  ) : null}
                </div>
              </div>
              <div className="mt-3 grid gap-2 md:grid-cols-2">
                {(job.steps || []).map((step) => (
                  <div key={step.step_key} className="rounded-xl border border-border bg-card px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-medium text-muted-foreground">{step.display_name}</p>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${statusTone[step.status] || 'bg-muted text-muted-foreground'}`}>
                        {step.status}
                      </span>
                    </div>
                    {step.error_message ? <p className="mt-1 text-[11px] text-rose-600">{step.error_message}</p> : null}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  const renderServicesSection = () => (
    <Card className={PANEL_CLASS}>
      <CardHeader>
        <CardTitle>Service Registry</CardTitle>
        <CardDescription>Core services aligned to microservice boundaries and owning teams.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {(dashboard.services || []).map((service) => (
          <div key={service.service_key} className="rounded-2xl border border-border bg-muted p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-foreground">{service.display_name}</p>
                <p className="mt-1 text-xs text-muted-foreground">{service.service_type} • {service.runtime}</p>
              </div>
              <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium capitalize ${statusTone[service.status] || 'bg-muted text-muted-foreground'}`}>
                {service.status}
              </span>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <p className={LABEL_CLASS}>Owner</p>
                <p className="mt-1 text-sm text-foreground">{service.owning_team || 'Platform'}</p>
              </div>
              <div>
                <p className={LABEL_CLASS}>SLO</p>
                <p className="mt-1 text-sm text-foreground">{service.slo_target}%</p>
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );

  const renderNotificationsSection = () => (
    <Card className={PANEL_CLASS}>
      <CardHeader>
        <CardTitle>Notification Outbox</CardTitle>
        <CardDescription>Database-backed templates, provider configs, queue state, and delivery execution.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {(dashboard.notifications?.status_mix || []).map((item) => (
            <div key={item.name} className={`${SUB_PANEL_CLASS} p-4`}>
              <p className={LABEL_CLASS}>{item.name}</p>
              <p className="mt-2 text-2xl font-semibold text-foreground">{item.value}</p>
            </div>
          ))}
          <div className={`${SUB_PANEL_CLASS} p-4`}>
            <p className={LABEL_CLASS}>Templates</p>
            <p className="mt-2 text-2xl font-semibold text-foreground">{dashboard.notifications?.templates_total || 0}</p>
          </div>
          <div className={`${SUB_PANEL_CLASS} p-4`}>
            <p className={LABEL_CLASS}>Providers</p>
            <p className="mt-2 text-2xl font-semibold text-foreground">{dashboard.notifications?.providers_total || 0}</p>
          </div>
          <div className={`${SUB_PANEL_CLASS} p-4`}>
            <p className={LABEL_CLASS}>Attempts</p>
            <p className="mt-2 text-2xl font-semibold text-foreground">{dashboard.notifications?.attempts_total || 0}</p>
          </div>
        </div>
        <div className="grid gap-3 xl:grid-cols-2">
          <div className="space-y-3">
            {(dashboard.notifications?.provider_rows || []).map((provider) => (
              <div key={provider.provider_key} className="rounded-2xl border border-border bg-muted p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{provider.display_name}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{provider.channel} • {provider.provider_type}</p>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium capitalize ${provider.is_active ? 'bg-emerald-500/10 text-emerald-600' : 'bg-muted text-muted-foreground'}`}>
                    {provider.is_active ? 'active' : 'inactive'}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-3">
            {(dashboard.notifications?.template_rows || []).slice(0, 4).map((template) => (
              <div key={template.template_key} className="rounded-2xl border border-border bg-muted p-4">
                <p className="text-sm font-semibold text-foreground">{template.display_name}</p>
                <p className="mt-1 text-xs text-muted-foreground">{template.channel} • {template.template_key}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-3">
          {(dashboard.notifications?.recent || []).map((row) => (
            <div key={row.notification_uuid} className="rounded-2xl border border-border bg-muted p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-foreground">{row.subject || row.template_key || row.recipient}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{row.channel} • {row.provider} • {row.recipient}</p>
                  {row.body_text ? <p className="mt-2 text-xs leading-5 text-muted-foreground">{row.body_text}</p> : null}
                  {row.last_error ? <p className="mt-2 text-xs leading-5 text-rose-600">{row.last_error}</p> : null}
                </div>
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium capitalize ${statusTone[row.status] || 'bg-muted text-muted-foreground'}`}>
                  {row.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  const renderStorageSection = () => (
    <Card className={PANEL_CLASS}>
      <CardHeader>
        <CardTitle>Storage & Activity</CardTitle>
        <CardDescription>Indexed assets and platform-level operational events.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className={`${SUB_PANEL_CLASS} p-4`}>
            <p className={LABEL_CLASS}>Stored Assets</p>
            <p className="mt-2 text-2xl font-semibold text-foreground">{dashboard.storage?.objects_total || 0}</p>
          </div>
          <div className={`${SUB_PANEL_CLASS} p-4`}>
            <p className={LABEL_CLASS}>Activity 7D</p>
            <p className="mt-2 text-2xl font-semibold text-foreground">{dashboard.activity?.events_7d || 0}</p>
          </div>
        </div>
        <div className="space-y-3">
          {(dashboard.storage?.recent_assets || []).slice(0, 3).map((asset) => (
            <div key={asset.object_key} className="rounded-2xl border border-border bg-muted p-4">
              <p className="text-sm font-semibold text-foreground">{asset.original_file_name || asset.object_key}</p>
              <p className="mt-1 text-xs text-muted-foreground">{asset.storage_provider} • {Math.round((asset.bytes_size || 0) / 1024)} KB</p>
            </div>
          ))}
          {(dashboard.activity?.recent || []).slice(0, 3).map((event) => (
            <div key={event.id} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-foreground">{event.summary}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{event.source_service} • {event.actor_email || 'system'}</p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium capitalize ${statusTone[event.severity] || 'bg-muted text-muted-foreground'}`}>
                  {event.severity}
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  const renderActiveSection = () => {
    switch (currentSectionKey) {
      case 'foundation':
        return renderFoundationSection();
      case 'monetization':
        return renderMonetizationSection();
      case 'provisioning':
        return renderProvisioningSection();
      case 'services':
        return renderServicesSection();
      case 'notifications':
        return renderNotificationsSection();
      case 'storage':
        return renderStorageSection();
      case 'overview':
      default:
        return renderOverviewSection();
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <StatSkeletonGrid count={4} />
        <StatSkeletonGrid count={4} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div>
          <p className={LABEL_CLASS}>Platform Core</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-foreground">{currentSection.label}</h1>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">{currentSection.description}</p>
        </div>

        {currentSectionKey === 'overview' ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            {navigationCards.map((section) => {
              const Icon = section.icon;
              const isActive = section.key === currentSectionKey;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => onNavigate?.(section.id)}
                  className={`card-interactive rounded-2xl border p-4 text-left transition ${
                    isActive
                      ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                      : 'border-border bg-card text-foreground hover:border-primary/40 hover:bg-muted'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className={`text-xs font-medium uppercase tracking-[0.18em] ${isActive ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                        {section.label}
                      </p>
                      <p className={`mt-2 text-sm font-semibold ${isActive ? 'text-primary-foreground' : 'text-foreground'}`}>{section.metric}</p>
                    </div>
                    <Icon className={`h-5 w-5 ${isActive ? 'text-primary-foreground' : 'text-muted-foreground'}`} />
                  </div>
                  <p className={`mt-3 text-sm ${isActive ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>{section.description}</p>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      {renderActiveSection()}
    </div>
  );
};

const currentUserEmailFallback = (dashboard) =>
  dashboard.activity?.recent?.find((event) => event.actor_email)?.actor_email || 'superadmin@appable.local';

export default SuperadminPlatformCorePage;
